import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import moment from "moment";
import {
  TbArrowLeft,
  TbArrowRight,
  TbDownload,
  TbFileText,
} from "react-icons/tb";

import {
  CustomTab,
  CustomMRTTable,
  CustomModal,
  CustomTextInput,
  CustomButton,
  CustomFileInput,
  CustomDatePicker,
  CustomRadioGroup,
  CustomLoader,
} from "../../components";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../services/Api";
import styles from "./plans-pricing/PlansPricing.module.css";

const LIST_PATH = -1;
const VIEW_CLIENT_API = "/saas/view/client";
const UPDATE_STATUS_API = "/saas/update/client/status";
const LIST_TRANSACTION_HISTORY_API = "/saas/list/transaction-history";
const PAYMENT_HISTORY_API = "/saas/purchase-order/payment-history";
const PAYMENT_RECEIPT_API = "/saas/payment-recepit";
const ADD_DISCUSSION_API = "/saas/add/discussion";
const UPLOAD_API = "/saas/discussion/upload";

const TABS = [
  { value: "details", label: "School Details" },
  { value: "transactions", label: "Transaction History" },
  { value: "billing", label: "Billing History" },
];

const STATUS_LABELS = {
  1: "Active",
  2: "Expired",
  3: "In Discussion",
  0: "Inactive",
};
const STATUS_COLOR_BY_ID = { 1: "green", 2: "red", 3: "orange", 0: "grey" };

const STATUS_PILL_STYLES = {
  green: { bg: "#D1FAE5", color: "#059669" },
  red: { bg: "#FEE2E2", color: "#DC2626" },
  orange: { bg: "#FFEDD5", color: "#C2410C" },
  blue: { bg: "#DBEAFE", color: "#1D4ED8" },
  grey: { bg: "#F3F4F6", color: "#4B5563" },
};

// Manual status changes are limited to the CRM-managed states — "Active" is
// driven by the actual purchase/payment flow, not this dropdown.
const MANUAL_STATUS_OPTIONS = [
  { value: "2", label: "Expired" },
  { value: "3", label: "In Discussion" },
  { value: "0", label: "Inactive" },
];

const WarningIcon = () => (
  <svg
    width="70"
    height="70"
    viewBox="0 0 70 70"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="35"
      cy="35"
      r="32"
      stroke="#F5821F"
      strokeWidth="2.5"
      fill="none"
    />
    <line
      x1="35"
      y1="20"
      x2="35"
      y2="41"
      stroke="#F5821F"
      strokeWidth="3.5"
      strokeLinecap="round"
    />
    <circle cx="35" cy="50" r="3" fill="#F5821F" />
  </svg>
);

const formatDateLong = (value) => {
  if (!value) return "—";
  const parsed = moment(value, ["DD/MM/YYYY", moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : value;
};

const getUserId = () => {
  const user = TokenService.getUser();
  return user?.id || user?.user_id || 1;
};

// Uploaded files resolve against the API's origin (e.g. https://dev.ifillip.com),
// not the versioned /v1/api/ base — verified against a real upload_path.
const UPLOAD_ORIGIN = (() => {
  try {
    return new URL(IMAGE_BASE_URL).origin;
  } catch {
    return IMAGE_BASE_URL.replace(/\/+$/, "").replace(/\/api$/, "");
  }
})();

const dedupeSlashes = (url) => {
  const m = url.match(/^(https?:\/\/)(.*)$/i);
  if (!m) return url.replace(/\/{2,}/g, "/");
  return m[1] + m[2].replace(/\/{2,}/g, "/");
};

const normalizeAttachment = (attachment) => {
  if (!attachment) return null;
  if (typeof attachment === "string") return { path: attachment, name: null };
  if (Array.isArray(attachment)) return normalizeAttachment(attachment[0]);
  if (typeof attachment === "object") {
    return {
      // Prefer an already-absolute URL; fall back to relative paths.
      path:
        attachment.file_url ||
        attachment.url ||
        attachment.upload_path ||
        attachment.path ||
        attachment.file_path ||
        null,
      name:
        attachment.file_name ||
        attachment.original_name ||
        attachment.name ||
        null,
    };
  }
  return null;
};

const getAttachmentUrl = (attachment) => {
  const normalized = normalizeAttachment(attachment);
  if (!normalized?.path) return null;
  if (/^https?:\/\//i.test(normalized.path))
    return dedupeSlashes(normalized.path);
  const cleanPath = normalized.path.startsWith("/")
    ? normalized.path
    : `/${normalized.path}`;
  return dedupeSlashes(`${UPLOAD_ORIGIN}${cleanPath}`);
};

const getAttachmentName = (attachment) => {
  const normalized = normalizeAttachment(attachment);
  if (!normalized) return null;
  if (normalized.name) return normalized.name;
  if (!normalized.path) return null;
  const parts = normalized.path.split("/");
  return parts[parts.length - 1] || normalized.path;
};

const StatusPill = ({ label, color }) => {
  const { bg, color: text } =
    STATUS_PILL_STYLES[color] || STATUS_PILL_STYLES.grey;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 14px",
        borderRadius: "999px",
        fontSize: "0.8125rem",
        fontWeight: 600,
        backgroundColor: bg,
        color: text,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

const OutlineStatusChip = ({ label, color }) => {
  const { color: text } = STATUS_PILL_STYLES[color] || STATUS_PILL_STYLES.grey;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 12px",
        borderRadius: "6px",
        fontSize: "0.75rem",
        fontWeight: 600,
        border: `1px solid ${text}`,
        color: text,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

// Attachment as a bordered card: file icon · name · download icon.
const AttachmentCard = ({ url, name }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    title={name || "Download attachment"}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "10px",
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: "8px",
      padding: "9px 14px",
      fontSize: "0.8125rem",
      color: "var(--color-text-primary)",
      textDecoration: "none",
      maxWidth: "320px",
    }}
  >
    <TbFileText size={20} color="#6B7280" style={{ flexShrink: 0 }} />
    <span
      style={{
        flex: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {name || "Attachment"}
    </span>
    <TbDownload size={18} color="#6B7280" style={{ flexShrink: 0 }} />
  </a>
);

const InfoField = ({ label, value }) => (
  <div>
    <div
      style={{
        fontSize: "0.75rem",
        color: "var(--color-text-secondary)",
        marginBottom: "4px",
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: "0.875rem",
        fontWeight: 600,
        color: "var(--color-text-primary)",
      }}
    >
      {value || "—"}
    </div>
  </div>
);

const InfoSection = ({ title, children }) => (
  <div className={styles.card}>
    <h2 className={styles.sectionTitle}>{title}</h2>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "20px 24px",
      }}
    >
      {children}
    </div>
  </div>
);

const BILLING_COLUMNS = [
  {
    accessorKey: "receipt_no",
    header: "Receipt No",
    size: 130,
    Cell: ({ cell }) => cell.getValue() ?? "—",
  },
  {
    accessorKey: "purchase_date",
    header: "Purchase Date",
    size: 130,
    Cell: ({ cell }) => cell.getValue() ?? "—",
  },
  {
    accessorKey: "subscription_period",
    header: "Subscription Period",
    size: 190,
    Cell: ({ cell }) => cell.getValue() ?? "—",
  },
  {
    accessorKey: "plan",
    header: "Plans",
    size: 130,
    Cell: ({ cell }) => cell.getValue() ?? "—",
  },
  {
    accessorKey: "users_count",
    header: "Users Count",
    size: 120,
    Cell: ({ cell }) => cell.getValue() ?? "—",
  },
  {
    accessorKey: "add_ons",
    header: "Add-ons",
    size: 200,
    Cell: ({ row }) => {
      const count = row.original.add_ons;
      if (!count) return "—";
      return row.original.addon_names
        ? `${count} (${row.original.addon_names})`
        : count;
    },
  },
  {
    accessorKey: "payment_type",
    header: "Payment Type",
    size: 130,
    Cell: ({ cell }) =>
      cell.getValue() ? cell.getValue().replace(/_/g, " ") : "—",
  },
  {
    accessorKey: "amount",
    header: "Amount",
    size: 110,
    Cell: ({ cell }) => (cell.getValue() != null ? `₹${cell.getValue()}` : "—"),
  },
  {
    accessorKey: "renewal_date",
    header: "Renewal Date",
    size: 130,
    Cell: ({ cell }) => cell.getValue() ?? "—",
  },
];

const getBillingColumns = (onDownloadReceipt, downloadingPaymentId) => [
  ...BILLING_COLUMNS,
  {
    id: "actions",
    header: "Action",
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    Cell: ({ row }) => {
      const paymentId = row.original.payment_id;
      const isDownloading = downloadingPaymentId === paymentId;
      return (
        <button
          type="button"
          disabled={!paymentId || isDownloading}
          onClick={() => onDownloadReceipt(paymentId)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            background: "#F5821F",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            padding: "6px 12px",
            fontSize: "0.8125rem",
            fontWeight: 600,
            cursor: paymentId && !isDownloading ? "pointer" : "not-allowed",
            opacity: paymentId ? (isDownloading ? 0.7 : 1) : 0.6,
          }}
        >
          <TbDownload size={14} /> {isDownloading ? "..." : "PDF"}
        </button>
      );
    },
  },
];

const SchoolView = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const schoolFromList = location.state?.school || null;

  // Seed the header instantly from the list row so the page paints before
  // /saas/view/client resolves; the fetch below overwrites it with the
  // authoritative record (and fills in school_details, which the list
  // endpoint doesn't return at all).
  const [header, setHeader] = useState(
    schoolFromList
      ? {
          school_name: schoolFromList.school_name,
          status_id: schoolFromList.status_id,
          status_label: schoolFromList.status_label,
          status_color: schoolFromList.status_color,
          purchase_date: schoolFromList.purchase_date,
          expire_date: schoolFromList.renew_date,
        }
      : null,
  );
  const [details, setDetails] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState(null);

  const [transactionHistory, setTransactionHistory] = useState([]);
  const [transactionLoading, setTransactionLoading] = useState(true);
  const [transactionError, setTransactionError] = useState(null);
  const [activeTab, setActiveTab] = useState("details");

  const [billingHistory, setBillingHistory] = useState([]);
  const [billingTotal, setBillingTotal] = useState(0);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingError, setBillingError] = useState(null);
  const [billingPagination, setBillingPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [downloadingPaymentId, setDownloadingPaymentId] = useState(null);

  const fetchTransactionHistory = useCallback(async () => {
    setTransactionLoading(true);
    setTransactionError(null);
    try {
      const response = await AxiosInstance.post(LIST_TRANSACTION_HISTORY_API, {
        purchase_order_id: Number(id),
        page: 1,
        limit: 50,
      });

      if (response.data?.status && response.data.data) {
        setTransactionHistory(response.data.data.list || []);
      } else {
        throw new Error(
          response.data?.message || "Failed to fetch transaction history",
        );
      }
    } catch (err) {
      console.error("Error fetching transaction history:", err);
      setTransactionError(
        err?.response?.data?.message || err?.message || "An error occurred",
      );
    } finally {
      setTransactionLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTransactionHistory();
  }, [fetchTransactionHistory]);

  const fetchBillingHistory = useCallback(async () => {
    setBillingLoading(true);
    setBillingError(null);
    try {
      const response = await AxiosInstance.post(PAYMENT_HISTORY_API, {
        purchase_order_id: Number(id),
        page: billingPagination.pageIndex + 1,
        limit: billingPagination.pageSize,
      });

      if (response.data?.status && response.data.data) {
        setBillingHistory(response.data.data.list || []);
        setBillingTotal(response.data.data.pagination?.total || 0);
      } else {
        throw new Error(
          response.data?.message || "Failed to fetch billing history",
        );
      }
    } catch (err) {
      console.error("Error fetching billing history:", err);
      setBillingError(
        err?.response?.data?.message || err?.message || "An error occurred",
      );
      setBillingHistory([]);
      setBillingTotal(0);
    } finally {
      setBillingLoading(false);
    }
  }, [id, billingPagination.pageIndex, billingPagination.pageSize]);

  useEffect(() => {
    fetchBillingHistory();
  }, [fetchBillingHistory]);

  const handleDownloadReceipt = useCallback(async (paymentId) => {
    if (!paymentId) return;

    setDownloadingPaymentId(paymentId);
    try {
      const response = await AxiosInstance.post(PAYMENT_RECEIPT_API, {
        id: paymentId,
      });
      const html = typeof response.data === "string" ? response.data : "";

      if (!html) throw new Error("Receipt is unavailable");

      // Printed via a hidden iframe so nothing opens in a new tab/window —
      // the user stays on this page and only the browser's print dialog appears.
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const cleanup = () => iframe.remove();

      iframe.onload = () => {
        iframe.contentWindow.onafterprint = cleanup;
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Safety net in case `afterprint` never fires (e.g. dialog dismissed via Esc in some browsers).
        setTimeout(cleanup, 60000);
      };
      iframe.srcdoc = html;
    } catch (err) {
      console.error("Error fetching payment receipt:", err);
    } finally {
      setDownloadingPaymentId(null);
    }
  }, []);

  const billingColumns = useMemo(
    () => getBillingColumns(handleDownloadReceipt, downloadingPaymentId),
    [handleDownloadReceipt, downloadingPaymentId],
  );

  const fetchSchoolDetail = useCallback(async () => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const response = await AxiosInstance.post(VIEW_CLIENT_API, {
        id: Number(id),
      });

      if (response.data?.status && response.data.data) {
        setHeader(response.data.data.header || null);
        setDetails(response.data.data.school_details || null);
      } else {
        throw new Error(
          response.data?.message || "Failed to fetch school details",
        );
      }
    } catch (err) {
      console.error("Error fetching school details:", err);
      setDetailError(
        err?.response?.data?.message || err?.message || "An error occurred",
      );
    } finally {
      setDetailLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSchoolDetail();
  }, [fetchSchoolDetail]);

  // ── Change Status modal ───────────────────────────────────────────────────
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [feedback, setFeedback] = useState("");
  const [feedbackError, setFeedbackError] = useState("");
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  // ── Add Discussion Record modal ───────────────────────────────────────────
  const [discussionModalOpen, setDiscussionModalOpen] = useState(false);
  const [discussionMessage, setDiscussionMessage] = useState("");
  const [discussionMessageError, setDiscussionMessageError] = useState("");
  const [nextFollowUpDate, setNextFollowUpDate] = useState(null);
  const [nextFollowUpDateError, setNextFollowUpDateError] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentUploadPath, setAttachmentUploadPath] = useState(null);
  const [attachmentOriginalName, setAttachmentOriginalName] = useState(null);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentUploadError, setAttachmentUploadError] = useState("");
  const [discussionSubmitting, setDiscussionSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const statusId = header?.status_id ?? null;
  const statusLabel = header?.status_label || STATUS_LABELS[statusId] || "—";
  const statusColor =
    header?.status_color || STATUS_COLOR_BY_ID[statusId] || "grey";

  const availableStatusOptions = useMemo(
    () => MANUAL_STATUS_OPTIONS.filter((opt) => Number(opt.value) !== statusId),
    [statusId],
  );

  const accountDetails = details?.account_details;
  const schoolInfo = details?.school_information;
  const adminDetails = details?.admin_details;
  const domainDetails = details?.domain_details;
  const shortNameUrl =
    domainDetails?.sub_domain_url ||
    (domainDetails?.short_name
      ? `${domainDetails.short_name}.ifillip.com`
      : null);

  const openStatusModal = () => {
    setNewStatus(availableStatusOptions[0]?.value || "");
    setFeedback("");
    setFeedbackError("");
    setStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    if (statusSubmitting) return;
    setStatusModalOpen(false);
  };

  const handleConfirmStatusChange = async () => {
    if (!feedback.trim()) {
      setFeedbackError("Reason is required");
      return;
    }
    setStatusSubmitting(true);
    try {
      const response = await AxiosInstance.put(UPDATE_STATUS_API, {
        id: Number(id),
        status: Number(newStatus),
        feedback: feedback.trim(),
      });

      if (response.data?.status) {
        const nextId = Number(newStatus);

        setHeader((prev) => ({
          ...prev,
          status_id: nextId,
          status_label: STATUS_LABELS[nextId],
          status_color: STATUS_COLOR_BY_ID[nextId],
        }));

        setStatusModalOpen(false);
        setFeedback("");
        fetchTransactionHistory();
      } else {
        throw new Error(response.data?.message || "Failed to update status");
      }
    } catch (err) {
      setFeedbackError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update status",
      );
    } finally {
      setStatusSubmitting(false);
    }
  };

  const openDiscussionModal = () => {
    setDiscussionMessage("");
    setDiscussionMessageError("");
    setNextFollowUpDate(null);
    setNextFollowUpDateError("");
    setAttachment(null);
    setAttachmentUploadPath(null);
    setAttachmentOriginalName(null);
    setAttachmentUploadError("");
    setCalendarOpen(false);
    setDiscussionModalOpen(true);
  };

  const closeDiscussionModal = () => {
    if (discussionSubmitting) return;
    setCalendarOpen(false);
    setDiscussionModalOpen(false);
  };

  const handleAttachmentChange = async (file) => {
    setAttachment(file);
    setAttachmentUploadPath(null);
    setAttachmentOriginalName(null);
    setAttachmentUploadError("");

    if (!file) return;

    setAttachmentUploading(true);
    try {
      const formDataPayload = new FormData();
      formDataPayload.append("discussion_file", file);

      const response = await AxiosInstance.post(UPLOAD_API, formDataPayload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data?.status && response.data.data?.discussion_file_path) {
        setAttachmentUploadPath(response.data.data.discussion_file_path);
        setAttachmentOriginalName(response.data.data.discussion_file || null);
      } else {
        throw new Error(response.data?.message || "Invalid upload response");
      }
    } catch (err) {
      console.error("Attachment upload error:", err);
      setAttachmentUploadError(
        "Failed to upload attachment. Please try again.",
      );
    } finally {
      setAttachmentUploading(false);
    }
  };

  const handleSaveDiscussion = async () => {
    let hasError = false;
    if (!discussionMessage.trim()) {
      setDiscussionMessageError("Follow up message is required");
      hasError = true;
    }
    if (!nextFollowUpDate) {
      setNextFollowUpDateError("Next follow-up date is required");
      hasError = true;
    }
    if (attachment && attachmentUploading) {
      setAttachmentUploadError(
        "Please wait for the attachment to finish uploading",
      );
      hasError = true;
    }
    if (attachment && !attachmentUploadPath && !attachmentUploading) {
      setAttachmentUploadError(
        "Attachment upload failed — remove it or try again",
      );
      hasError = true;
    }
    if (hasError) return;

    setDiscussionSubmitting(true);
    try {
      const payload = {
        purchase_order_id: Number(id),
        follow_up_message: discussionMessage.trim(),
        next_follow_up_date: nextFollowUpDate.format("YYYY-MM-DD"),
        created_by: getUserId(),
      };
      if (attachmentUploadPath) {
        payload.attachment_original_name = attachmentOriginalName;
        payload.attachment_path = attachmentUploadPath;
      }

      const response = await AxiosInstance.post(ADD_DISCUSSION_API, payload);

      if (response.data?.status) {
        setDiscussionModalOpen(false);
        fetchTransactionHistory();
      } else {
        throw new Error(
          response.data?.message || "Failed to save discussion record",
        );
      }
    } catch (err) {
      setDiscussionMessageError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save discussion record",
      );
    } finally {
      setDiscussionSubmitting(false);
    }
  };

  if (detailLoading && !header) {
    return (
      <div className={styles.loadingContainer}>
        <CustomLoader text="Loading school details..." />
      </div>
    );
  }

  if (!header) {
    return (
      <div>
        <div className={styles.card}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(LIST_PATH)}
          >
            <TbArrowLeft size={22} />
          </button>
          <p className={styles.confirmText} style={{ marginTop: "12px" }}>
            {detailError || "School details aren't available."}
          </p>
          <CustomButton
            variant="primary"
            fullWidth={false}
            className={styles.btnOrange}
            onClick={fetchSchoolDetail}
          >
            Retry
          </CustomButton>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* ── Header ── */}
      <div className={styles.card}>
        <div className={styles.detailHeaderRow}>
          <div className={styles.detailHeaderLeft}>
            <button
              type="button"
              className={styles.backButton}
              onClick={() => navigate(LIST_PATH)}
            >
              <TbArrowLeft size={22} />
            </button>
            <h1 className={styles.detailTitle}>{header.school_name}</h1>
            <StatusPill label={statusLabel} color={statusColor} />
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <CustomButton
              variant="primary"
              fullWidth={false}
              className={styles.btnOrange}
              onClick={openStatusModal}
            >
              Change Status
            </CustomButton>
            {/* {statusId === 3 && ( */}
            <CustomButton
              variant="primary"
              fullWidth={false}
              className={styles.btnOrange}
              onClick={openDiscussionModal}
            >
              Add Discussion Record
            </CustomButton>
            {/* )} */}
          </div>
        </div>

        <div className={styles.metaRow}>
          Purchased on: <strong>{formatDateLong(header.purchase_date)}</strong>
          <span className={styles.metaSeparator}>•</span>
          Expired on: <strong>{formatDateLong(header.expire_date)}</strong>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className={styles.tabsWrapper}>
        <CustomTab tabs={TABS} value={activeTab} onChange={setActiveTab} />
      </div>

      {/* ── SCHOOL DETAILS ── */}
      {activeTab === "details" && (
        <div className={styles.tabPanel}>
          <InfoSection title="Account Details">
            <InfoField label="First Name" value={accountDetails?.first_name} />
            <InfoField label="Last Name" value={accountDetails?.last_name} />
            <InfoField
              label="Email Address"
              value={accountDetails?.email_address}
            />
            <InfoField
              label="Phone Number"
              value={accountDetails?.phone_number}
            />
          </InfoSection>

          <InfoSection title="School Details">
            <InfoField label="School Name" value={schoolInfo?.school_name} />
            <InfoField
              label="School Board"
              value={schoolInfo?.school_board_name}
            />
            <InfoField
              label="School Category"
              value={schoolInfo?.school_category_name}
            />
            <InfoField
              label="School Address"
              value={schoolInfo?.school_address}
            />
            <InfoField label="City" value={schoolInfo?.city} />
            <InfoField label="State" value={schoolInfo?.state_name} />
            <InfoField label="Pincode" value={schoolInfo?.pincode} />
            <InfoField label="UDISE Code" value={schoolInfo?.udise_no} />
          </InfoSection>

          <InfoSection title="Contact Details">
            <InfoField
              label="Mobile Number"
              value={accountDetails?.phone_number}
            />
            <InfoField label="Short Name URL" value={shortNameUrl} />
          </InfoSection>

          <InfoSection title="Billing Details">
            <InfoField label="GSTIN" value={schoolInfo?.gst_number} />
          </InfoSection>

          <InfoSection title="School Admin Contact Details">
            <InfoField label="First Name" value={adminDetails?.first_name} />
            <InfoField label="Last Name" value={adminDetails?.last_name} />
            <InfoField
              label="Email Address"
              value={adminDetails?.email_address}
            />
            <InfoField
              label="Phone Number"
              value={adminDetails?.phone_number}
            />
          </InfoSection>
        </div>
      )}

      {/* ── TRANSACTION HISTORY ── */}
      {activeTab === "transactions" && (
        <div className={styles.tabPanel}>
          <div className={styles.card}>
            <h2 className={styles.sectionTitle}>History Transaction</h2>

            {transactionLoading ? (
              <div className={styles.accordionEmpty}>
                Loading transaction history...
              </div>
            ) : transactionError ? (
              <div className={styles.accordionEmpty}>{transactionError}</div>
            ) : transactionHistory.length === 0 ? (
              <div className={styles.accordionEmpty}>
                No transaction history yet.
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "18px",
                }}
              >
                {transactionHistory.map((entry) => {
                  // Resolve once per entry so the guard and the link agree.
                  const attachmentUrl = getAttachmentUrl(entry.attachment);
                  const attachmentName = getAttachmentName(entry.attachment);

                  return (
                    <div key={entry.id}>
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "var(--color-text-tertiary)",
                          marginBottom: "6px",
                        }}
                      >
                        {entry.created_on}
                      </div>

                      {entry.transaction_type_key === "status_change" ? (
                        <div
                          style={{
                            background: "var(--color-surface-soft)",
                            border: "1px solid var(--color-border-soft)",
                            borderRadius: "10px",
                            padding: "14px 16px",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              marginBottom: "10px",
                            }}
                          >
                            {entry.title || "Changed the Status"}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              marginBottom: "10px",
                            }}
                          >
                            <OutlineStatusChip
                              label={entry.from_status_label}
                              color={entry.from_status_color}
                            />
                            <TbArrowRight
                              size={16}
                              color="var(--color-text-secondary)"
                            />
                            <OutlineStatusChip
                              label={entry.to_status_label}
                              color={entry.to_status_color}
                            />
                          </div>
                          <div
                            style={{
                              fontSize: "0.8125rem",
                              color: "var(--color-text-primary)",
                            }}
                          >
                            Reason : {entry.feedback}
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            background: "var(--color-surface-soft)",
                            border: "1px solid var(--color-border-soft)",
                            borderRadius: "10px",
                            padding: "14px 16px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "0.8125rem",
                              color: "var(--color-text-primary)",
                              marginBottom: "10px",
                            }}
                          >
                            {entry.follow_up_message}
                          </div>
                          <div
                            style={{
                              fontSize: "0.8125rem",
                              fontWeight: 600,
                              marginBottom: getAttachmentUrl(entry.attachment)
                                ? "10px"
                                : 0,
                            }}
                          >
                            Next Follow up date : {entry.next_follow_up_date}
                          </div>
                          {attachmentUrl && (
                            <AttachmentCard
                              url={attachmentUrl}
                              name={attachmentName}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── BILLING HISTORY ── */}
      {activeTab === "billing" && (
        <div className={styles.tabPanel}>
          <div className={styles.card}>
            <h2 className={styles.sectionTitle}>Billing History</h2>

            {billingError ? (
              <div
                style={{
                  color: "#DC2626",
                  fontSize: "14px",
                  marginBottom: "16px",
                }}
              >
                {billingError}
                <button
                  type="button"
                  onClick={fetchBillingHistory}
                  style={{
                    marginLeft: "12px",
                    border: "1px solid #DC2626",
                    background: "transparent",
                    color: "#DC2626",
                    borderRadius: "6px",
                    padding: "4px 12px",
                    cursor: "pointer",
                  }}
                >
                  Retry
                </button>
              </div>
            ) : null}

            <CustomMRTTable
              columns={billingColumns}
              data={billingHistory}
              rowCount={billingTotal}
              loading={billingLoading}
              manualPagination
              manualFiltering={false}
              pagination={billingPagination}
              onPaginationChange={setBillingPagination}
              enablePagination
              enableSorting={false}
              enableSearch={false}
              enableColumnFilters={false}
              enableColumnVisibility={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableHiding={false}
              enableColumnActions={false}
              enableStickyHeader
            />
          </div>
        </div>
      )}

      {/* ── Change Status modal ── */}
      <CustomModal
        isOpen={statusModalOpen}
        onClose={closeStatusModal}
        title=""
        size="md"
        showCloseButton={false}
      >
        <div style={{ textAlign: "center", padding: "24px 24px 8px" }}>
          <div className={styles.warningIconWrap}>
            <WarningIcon />
          </div>
          <h2 className={styles.confirmTitle}>Change School Status ?</h2>
          <p className={styles.confirmTextCentered}>
            This action is permanent and cannot be reversed. Once the status is
            changed, the school cannot be restored to its previous status.
          </p>

          <div style={{ textAlign: "left", marginBottom: "16px" }}>
            <CustomRadioGroup
              name="newStatus"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              options={availableStatusOptions}
              row={false}
            />
          </div>

          <CustomTextInput
            label="Reason"
            name="feedback"
            multiline
            rows={3}
            required
            placeholder="Enter Reason"
            value={feedback}
            onChange={(e) => {
              setFeedback(e.target.value);
              if (feedbackError) setFeedbackError("");
            }}
            error={feedbackError}
          />

          <div
            className={styles.confirmActionsCentered}
            style={{ marginTop: "20px" }}
          >
            <CustomButton
              variant="outline-dark"
              fullWidth={false}
              onClick={closeStatusModal}
              disabled={statusSubmitting}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              fullWidth={false}
              className={styles.btnOrange}
              onClick={handleConfirmStatusChange}
              loading={statusSubmitting}
              loadingText="Updating..."
            >
              Change Status
            </CustomButton>
          </div>
        </div>
      </CustomModal>

      {/* ── Add Discussion Record modal ── */}
      <CustomModal
        isOpen={discussionModalOpen}
        onClose={closeDiscussionModal}
        title="Add Discussion Record"
        size="md"
      >
        <CustomTextInput
          label="Follow up message"
          name="discussionMessage"
          multiline
          rows={4}
          required
          placeholder="Type follow up message here..."
          value={discussionMessage}
          onChange={(e) => {
            setDiscussionMessage(e.target.value);
            if (discussionMessageError) setDiscussionMessageError("");
          }}
          error={discussionMessageError}
        />

        <CustomDatePicker
          label="Next follow-up date"
          name="nextFollowUpDate"
          required
          value={nextFollowUpDate}
          onChange={(value) => {
            setNextFollowUpDate(value);
            if (nextFollowUpDateError) setNextFollowUpDateError("");
          }}
          minDate={dayjs()}
          error={nextFollowUpDateError}
          onOpen={() => setCalendarOpen(true)} // ← add
          onClose={() => setCalendarOpen(false)} // ← add
        />
        {calendarOpen && <div style={{ height: 300 }} aria-hidden="true" />}
        <CustomFileInput
          label="Attachments"
          name="attachment"
          value={attachment}
          onChange={(file) => handleAttachmentChange(file)}
          disabled={attachmentUploading}
          error={attachmentUploadError}
          helperText={attachmentUploading ? "Uploading..." : undefined}
        />

        <div
          className={styles.confirmActionsCentered}
          style={{ marginTop: "20px" }}
        >
          <CustomButton
            variant="outline-dark"
            fullWidth={false}
            onClick={closeDiscussionModal}
            disabled={discussionSubmitting}
          >
            Cancel
          </CustomButton>
          <CustomButton
            variant="primary"
            fullWidth={false}
            className={styles.btnOrange}
            onClick={handleSaveDiscussion}
            loading={discussionSubmitting}
            loadingText="Saving..."
            disabled={attachmentUploading}
          >
            Save Conversation
          </CustomButton>
        </div>
      </CustomModal>
    </div>
  );
};

export default SchoolView;
